import type {
  SanityHardwareCardDocument,
  SanityHardwareComparisonSpec,
  SanityPageBuilderHardwareComparisonTableSection,
  SanityPageBuilderSection,
} from "@ouster/shared";

import {
  HardwareComparisonTableBlockView,
  type HardwareComparisonProduct,
  type HardwareComparisonRow,
} from "~/features/blocks/inventory/hardware-comparison-table";
import { toCraftAsset } from "~/features/sanity/media";

type Props = {
  section: SanityPageBuilderSection & {
    loaded?: boolean;
    sectionContent: SanityPageBuilderHardwareComparisonTableSection;
  };
};

function toRow(spec: SanityHardwareComparisonSpec): HardwareComparisonRow {
  return {
    key: spec._key,
    label: spec.label,
    icon: spec.icon,
    customIcon: toCraftAsset(spec.customIcon) as HardwareComparisonRow["customIcon"],
  };
}

function toProduct(product: SanityHardwareCardDocument): HardwareComparisonProduct {
  return {
    id: product._id,
    url: product.uri,
    downloadSpecsLink: product.downloadSpecsLink,
    title: product.title,
    productCaption: product.productCaption,
    productImage: toCraftAsset(product.productImage) as HardwareComparisonProduct["productImage"],
    productHighlights: product.productHighlights,
    specValues: product.specValues,
  };
}

export function SanityHardwareComparisonTableSection({ section }: Props) {
  const { sectionContent, sectionSettings } = section;
  const rows = (sectionContent.specs ?? []).map(toRow);
  const products = (sectionContent.products ?? []).map(toProduct);

  return (
    <HardwareComparisonTableBlockView
      anchor={{
        id: sectionSettings?.sectionId?.current || sectionSettings?.anchorName || section._key,
        name: sectionSettings?.anchorName,
      }}
      hasHighlightDifferencesToggle={sectionContent.hasHighlightDifferencesToggle}
      hasTopDivider={sectionSettings?.hasTopDivider ?? true}
      mode={sectionSettings?.mode || "light"}
      products={products}
      rows={rows}
      sectionProps={{
        "data-sanity-section-key": section._key,
        "data-sanity-section-type": section._type,
      }}
      theme={sectionSettings?.theme || "white"}
    />
  );
}
